import { useEffect } from "react";
import { usePublishedContentStore } from "./usePublishedContentStore";
import LogManager from "../utils/logManager";
import { callRouterLambda } from "../utils/routerLambdaUtils";

/**
 * usePublishedContent — manages UX Publish content store and catch-up fetch.
 *
 * IMPORTANT: This hook no longer owns a WebSocket connection. Messages are
 * dispatched to it by Home.jsx via the handlePublishMessage callback.
 * This prevents multiple useWebSocket calls from opening duplicate connections.
 *
 * Visibility filtering belongs to the backend, which uses the caller's alias from
 * X-FORWARDED-USER on both delivery paths. The client does NOT re-filter: the client
 * alias comes from the availability API, which attendees never call, so it's null for
 * them and would hide content they're entitled to see.
 *
 * @param {string|null} meetingUUID - meeting identifier
 * @param {boolean} wsEnabled - whether WebSocket push is active (controls catch-up fetch)
 * @returns {object} { ...store, handlePublishMessage }
 */
export function usePublishedContent(meetingUUID, wsEnabled) {
  const store = usePublishedContentStore();

  /**
   * Handle a single ux_publish message dispatched from the parent WebSocket.
   * Call this from the Home.jsx dispatcher when data.type === "ux_publish".
   */
  const handlePublishMessage = (data) => {
    const { extensionId, action, contentId } = data;
    if (!extensionId || !action || !contentId) {
      LogManager.warn("[PublishedContent] ux_publish missing required fields, ignoring:", JSON.stringify(data));
      return;
    }

    if (action === "upsert") {
      store.upsert(data);
    } else if (action === "remove") {
      store.remove(extensionId, contentId, data.updatedAt);
    } else {
      LogManager.info("[PublishedContent] Unknown ux_publish action:", action);
    }
  };

  // Catch-up fetch on activation
  useEffect(() => {
    if (!meetingUUID || !wsEnabled) return;

    const fetchPublishedContent = async () => {
      try {
        LogManager.info("[PublishedContent] Fetching catch-up for meeting:", meetingUUID);
        const envelope = await callRouterLambda("/getPublishedContent", { meetingUuid: meetingUUID });
        const items = envelope?.items;
        if (!Array.isArray(items)) {
          throw new Error("/getPublishedContent payload missing items array");
        }

        // Already filtered by GetPublishedContentLambda against the caller's
        // token-derived alias, so every item here is one this user may see.
        store.loadCatchUp(items);
      } catch (error) {
        LogManager.error("[PublishedContent] Catch-up fetch failed:", error);
      }
    };
    fetchPublishedContent();
  }, [meetingUUID, wsEnabled]);

  return { ...store, handlePublishMessage };
}
